// MODE.Cfix.B1 — DeliveryModeBadge + a plain-language HelpPopover.
//
// Composes the EXISTING DeliveryModeBadge and HelpPopover primitives (no new
// popover machinery — "copy + reuse"). Rendered in the project dashboard
// header so a user always sees WHAT their delivery mode means.
//
// Why this exists: the dashboard's other "What advancing means" HelpPopover
// is gated on the Hand-Off buttons being visible — a Governed-mode affordance
// that an Accelerate project never shows. So Accelerate users had a badge
// with no explanation of the (significant) autonomous behaviour. This
// explainer renders for BOTH modes and closes that gap.

import DeliveryModeBadge from './DeliveryModeBadge';
import { HelpPopover } from './help';

interface Props {
  mode: 'governed' | 'accelerate';
}

export default function DeliveryModeExplainer({ mode }: Props) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <DeliveryModeBadge mode={mode} />
      <HelpPopover
        id="delivery-mode-explainer"
        variant="info"
        heading={mode === 'accelerate' ? 'Accelerate Mode' : 'Governed Mode'}
        body={
          mode === 'accelerate' ? (
            <>
              <p style={{ margin: '0 0 8px' }}>
                This project is in <strong>Accelerate Mode</strong>. When a phase&apos;s exit gate passes,
                the Phase Gate Agent advances to the next phase <strong>automatically</strong> — you&apos;re
                notified after each transition rather than asked before it.
              </p>
              <p style={{ margin: 0 }}>
                Review still pauses at your project&apos;s Review Checkpoints. When loaded, the Autonomous
                Transitions panel below shows which phases auto-advance and which pause for your review.
              </p>
            </>
          ) : (
            <p style={{ margin: 0 }}>
              This project is in <strong>Governed Mode</strong>. Every phase exit waits for your approval —
              no phase advances to the next until you confirm it, even when the gate criteria pass.
            </p>
          )
        }
      />
    </span>
  );
}
